/**
 * Arabic (Morocco) formatting, aligned with arqam's src/documents/format.ts so
 * the screens say the same thing as the printed documents. Western digits
 * (ar-MA's default), dates in Morocco's timezone.
 */

const TZ = 'Africa/Casablanca';

let wholeDirhams = false;

/**
 * On phones (doc 52), "80 درهم" rather than "80,00 درهم": fewer digits to
 * read. Cents still show when there are some. The desk and the documents
 * keep both decimals. Set once by `startApp`.
 */
export function showWholeDirhams(on: boolean) {
  wholeDirhams = on;
}

/** "718,50 درهم". Money arrives as a BigFloat string; it is only displayed. */
export const dirhams = (amount: string | number | null | undefined) => {
  const n = Number(amount ?? 0);
  const decimals = wholeDirhams && Number.isInteger(n) ? 0 : 2;
  return `${n.toLocaleString('ar-MA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: 2,
  })} درهم`;
};

/** "12 م³" */
export const cubicMeters = (n: number | null | undefined) => `${n ?? 0} م³`;

/** "أكتوبر 2025" */
export const monthYear = (date: string | Date) =>
  new Date(date).toLocaleDateString('ar-MA', {
    timeZone: TZ,
    year: 'numeric',
    month: 'long',
  });

/** "31 أكتوبر 2025" */
export const fullDate = (date: string | Date) =>
  new Date(date).toLocaleDateString('ar-MA', {
    timeZone: TZ,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/** "15 ربيع الآخر 1448 هـ" (Umm al-Qura calendar, Western digits). */
export const hijriDate = (date: string | Date) =>
  new Date(date).toLocaleDateString('ar-MA-u-ca-islamic-umalqura-nu-latn', {
    timeZone: TZ,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/**
 * A billing month as "YYYY-MM". Bills store the month's last day as
 * `billMonth` (e.g. 2025-10-31), as the old app did.
 */
export type MonthKey = `${number}-${number}`;

export function currentMonth(now = new Date()): MonthKey {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}` as MonthKey;
}

export function addMonths(month: MonthKey, delta: number): MonthKey {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}` as MonthKey;
}

/** "2025-10-01" */
export const monthStart = (month: MonthKey) => `${month}-01`;

/** "2025-10-31": the `period` sent with a reading. */
export function monthEnd(month: MonthKey) {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${String(last).padStart(2, '0')}`;
}

/** "أكتوبر 2025" from "2025-10" (read at mid-month, safe in any timezone). */
export const monthLabel = (month: MonthKey) =>
  monthYear(`${month}-15T12:00:00Z`);

type CountedNoun = {
  one: string; // عداد واحد
  two: string; // عدادان
  few: string; // 3–10: عدادات
  many: string; // 11–99: عداداً
  hundred: string; // 100, 101, 102, …: عداد
};

/**
 * Arabic number agreement: the noun after a number depends on the number's
 * last two digits (1, 2, 3–10, 11–99, and 00–02 for 100 and above).
 */
export function counted(n: number, noun: CountedNoun): string {
  if (n === 1) return noun.one;
  if (n === 2) return noun.two;
  const lastTwo = n % 100;
  if (lastTwo >= 3 && lastTwo <= 10) return `${n} ${noun.few}`;
  if (lastTwo >= 11) return `${n} ${noun.many}`;
  return `${n} ${noun.hundred}`;
}

export const billsCount = (n: number) =>
  counted(n, {
    one: 'فاتورة واحدة',
    two: 'فاتورتان',
    few: 'فواتير',
    many: 'فاتورة',
    hundred: 'فاتورة',
  });

export const receiptsCount = (n: number) =>
  counted(n, {
    one: 'وصل واحد',
    two: 'وصلان',
    few: 'وصولات',
    many: 'وصلاً',
    hundred: 'وصل',
  });

export const metersCount = (n: number) =>
  counted(n, {
    one: 'عداد واحد',
    two: 'عدادان',
    few: 'عدادات',
    many: 'عداداً',
    hundred: 'عداد',
  });

/**
 * Midnight today in Morocco as an ISO timestamp with its offset
 * ("2026-09-28T00:00:00+01:00"), for "today's payments" filters. The offset
 * comes from the calendar (Morocco drops to +00:00 during Ramadan).
 */
export function startOfToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZoneName: 'longOffset',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const offset = get('timeZoneName').replace('GMT', '') || '+00:00';
  return `${get('year')}-${get('month')}-${get('day')}T00:00:00${offset}`;
}

export const membersCount = (n: number) =>
  counted(n, {
    one: 'عضو واحد',
    two: 'عضوان',
    few: 'أعضاء',
    many: 'عضواً',
    hundred: 'عضو',
  });

/** Same wording as arqam's documents (`daysCount`). */
export const daysCount = (n: number) =>
  counted(n, {
    one: 'يوم واحد',
    two: 'يومان',
    few: 'أيام',
    many: 'يوماً',
    hundred: 'يوم',
  });
